/**
 * Raptor Predator Frontend - TanStack Query Client
 * 
 * Configured for security dashboard with:
 * - Optimistic updates for fast UI
 * - Stale-while-revalidate for real-time data
 * - Offline persistence for PWA support
 */

import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { useUIStore } from '../stores/uiStore';

// ============================================================================
// Error Handler
// ============================================================================

function handleQueryError(error: unknown): void {
  const message = error instanceof Error ? error.message : 'An unknown error occurred';
  
  // Add notification
  useUIStore.getState().addNotification({
    type: 'error',
    title: 'Query Error',
    message,
    duration: 5000,
  });
  
  // Log for debugging
  console.error('[QueryClient] Error:', error);
}

function handleMutationError(error: unknown): void {
  const message = error instanceof Error ? error.message : 'An unknown error occurred';
  
  // Add notification
  useUIStore.getState().addNotification({
    type: 'error',
    title: 'Operation Failed',
    message,
    duration: 5000,
  });
  
  console.error('[QueryClient] Mutation Error:', error);
}

// ============================================================================
// Query Client Configuration
// ============================================================================

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 30 seconds for real-time data
      staleTime: 30 * 1000,
      
      // Cache time: 5 minutes
      gcTime: 5 * 60 * 1000,
      
      // Retry configuration
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus for real-time updates
      refetchOnWindowFocus: true,
      
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
      
      // Refetch in background when reconnecting
      refetchOnReconnect: true,
      
      // Network mode for offline support
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Retry failed mutations
      retry: 2,
      retryDelay: 1000,
      
      // Network mode
      networkMode: 'offlineFirst',
    },
  },
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
});

// ============================================================================
// Query Keys Factory
// ============================================================================

export const queryKeys = {
  // Graph data
  graph: {
    all: ['graph'] as const,
    nodes: () => [...queryKeys.graph.all, 'nodes'] as const,
    edges: () => [...queryKeys.graph.all, 'edges'] as const,
    node: (id: string) => [...queryKeys.graph.nodes(), id] as const,
    neighbors: (id: string) => [...queryKeys.graph.all, 'neighbors', id] as const,
  },
  
  // LDC data
  ldcs: {
    all: ['ldcs'] as const,
    list: () => [...queryKeys.ldcs.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.ldcs.all, id] as const,
    status: (id: string) => [...queryKeys.ldcs.all, id, 'status'] as const,
  },
  
  // Threats
  threats: {
    all: ['threats'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.threats.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.threats.all, id] as const,
    aggregated: () => [...queryKeys.threats.all, 'aggregated'] as const,
    byDevice: (deviceId: string) => [...queryKeys.threats.all, 'device', deviceId] as const,
  },
  
  // Remediations
  remediations: {
    all: ['remediations'] as const,
    active: () => [...queryKeys.remediations.all, 'active'] as const,
    history: () => [...queryKeys.remediations.all, 'history'] as const,
    detail: (id: string) => [...queryKeys.remediations.all, id] as const,
  },
  
  // Policies
  policies: {
    all: ['policies'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.policies.all, 'list', filters] as const,
    detail: (id: string) => [...queryKeys.policies.all, id] as const,
    effective: (targetId: string) => [...queryKeys.policies.all, 'effective', targetId] as const,
    conflicts: () => [...queryKeys.policies.all, 'conflicts'] as const,
  },
  
  // Guardrails
  guardrails: {
    all: ['guardrails'] as const,
    list: () => [...queryKeys.guardrails.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.guardrails.all, id] as const,
  },
  
  // Firewall
  firewall: {
    all: ['firewall'] as const,
    profiles: () => [...queryKeys.firewall.all, 'profiles'] as const,
    profile: (id: string) => [...queryKeys.firewall.all, 'profile', id] as const,
  },
  
  // Directory/LDAP
  directory: {
    all: ['directory'] as const,
    connections: () => [...queryKeys.directory.all, 'connections'] as const,
    connection: (id: string) => [...queryKeys.directory.all, 'connection', id] as const,
    health: (id: string) => [...queryKeys.directory.all, 'health', id] as const,
    objects: (connectionId: string, dn?: string) => [...queryKeys.directory.all, 'objects', connectionId, dn] as const,
  },
  
  // CCL & Virtual Networks
  ccl: {
    all: ['ccl'] as const,
    rules: () => [...queryKeys.ccl.all, 'rules'] as const,
    rule: (id: string) => [...queryKeys.ccl.all, 'rule', id] as const,
  },
  
  virtualNetworks: {
    all: ['virtualNetworks'] as const,
    list: () => [...queryKeys.virtualNetworks.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.virtualNetworks.all, id] as const,
  },
  
  // Inoculation
  inoculation: {
    all: ['inoculation'] as const,
    active: () => [...queryKeys.inoculation.all, 'active'] as const,
    history: () => [...queryKeys.inoculation.all, 'history'] as const,
    detail: (id: string) => [...queryKeys.inoculation.all, id] as const,
  },
  
  // Expert workflows
  workflows: {
    all: ['workflows'] as const,
    list: () => [...queryKeys.workflows.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.workflows.all, id] as const,
  },
  
  // System
  system: {
    all: ['system'] as const,
    health: () => [...queryKeys.system.all, 'health'] as const,
    version: () => [...queryKeys.system.all, 'version'] as const,
    metrics: () => [...queryKeys.system.all, 'metrics'] as const,
  },
} as const;

// ============================================================================
// Prefetch Helpers
// ============================================================================

export async function prefetchDashboardData(): Promise<void> {
  // Prefetch essential dashboard data
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: queryKeys.ldcs.list(),
      staleTime: 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.threats.aggregated(),
      staleTime: 30 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.system.health(),
      staleTime: 60 * 1000,
    }),
  ]);
}

export async function prefetchGraphData(): Promise<void> {
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: queryKeys.graph.nodes(),
      staleTime: 60 * 1000,
    }),
    queryClient.prefetchQuery({
      queryKey: queryKeys.graph.edges(),
      staleTime: 60 * 1000,
    }),
  ]);
}

// ============================================================================
// Invalidation Helpers
// ============================================================================

export function invalidateGraphData(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.graph.all });
}

export function invalidateThreatData(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.threats.all });
}

export function invalidatePolicyData(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.policies.all });
}

export function invalidateAllData(): void {
  void queryClient.invalidateQueries();
}

